'use client';
import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Eye, Receipt, CheckCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Invoice } from './PaymentsBillingContent';

interface Props {
  invoices: Invoice[];
  onSelect: (inv: Invoice) => void;
}

type SortKey = keyof Invoice;

const FEE_TYPE_COLORS: Record<string, string> = {
  'Burial Fee': 'bg-secondary text-primary',
  'Plot Purchase': 'bg-[#faf5ff] text-purple-700',
  'Maintenance Fee': 'bg-[#fffbeb] text-warning',
  'Plot Reservation': 'bg-[#f0f9ff] text-[#0369a1]',
};

export default function InvoiceTable({ invoices, onSelect }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('issuedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const perPage = 8;

  const sorted = [...invoices].sort((a, b) =>
    sortDir === 'asc'
      ? String(a[sortKey]).localeCompare(String(b[sortKey]))
      : String(b[sortKey]).localeCompare(String(a[sortKey]))
  );

  const totalPages = Math.ceil(sorted.length / perPage);
  const paged = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={10} className={sortKey === col && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'} />
      <ChevronDown size={10} className={sortKey === col && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'} />
    </span>
  );

  return (
    <div className="card-base overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {([
                ['invoiceNumber', 'Invoice No.'],
                ['accountHolder', 'Account Holder'],
                ['feeType', 'Fee Type'],
                ['amount', 'Amount'],
                ['issuedDate', 'Issued'],
                ['dueDate', 'Due Date'],
                ['cemetery', 'Cemetery'],
                ['graveId', 'Grave ID'],
                ['status', 'Status'],
              ] as [SortKey, string][]).map(([key, label]) => (
                <th
                  key={`th-inv-${key}`}
                  onClick={() => toggleSort(key)}
                  className="px-3 py-3 text-left text-[12px] font-600 text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground whitespace-nowrap"
                >
                  {label} <SortIcon col={key} />
                </th>
              ))}
              <th className="px-3 py-3 text-right text-[12px] font-600 text-muted-foreground uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paged.map((inv) => (
              <tr
                key={inv.id}
                className={`table-row-hover transition-colors ${inv.status === 'overdue' ? 'bg-[#fef2f2]/30' : ''}`}
              >
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs font-600 text-primary">{inv.invoiceNumber}</span>
                </td>
                <td className="px-3 py-3 font-600 text-foreground whitespace-nowrap">{inv.accountHolder}</td>
                <td className="px-3 py-3">
                  <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${FEE_TYPE_COLORS[inv.feeType] || 'bg-muted text-muted-foreground'}`}>
                    {inv.feeType}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-sm font-700 text-foreground">
                    R {inv.amount.toLocaleString('en-ZA')}
                  </span>
                </td>
                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{inv.issuedDate}</td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <span className={inv.status === 'overdue' ? 'text-negative font-600 text-xs' : 'text-muted-foreground text-xs'}>
                    {inv.dueDate}
                  </span>
                </td>
                <td className="px-3 py-3 text-muted-foreground">{inv.cemetery}</td>
                <td className="px-3 py-3">
                  <span className="font-mono-data text-xs text-muted-foreground">{inv.graveId}</span>
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={inv.status} size="sm" />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onSelect(inv)}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="View Invoice"
                    >
                      <Eye size={14} className="text-muted-foreground" />
                    </button>
                    <button
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                      title="View Receipt"
                    >
                      <Receipt size={14} className="text-muted-foreground" />
                    </button>
                    {inv.status !== 'paid' && (
                      <button
                        className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition-colors"
                        title="Mark as Paid"
                      >
                        <CheckCircle size={14} className="text-positive" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <span className="text-xs text-muted-foreground">
          Showing {Math.min((page - 1) * perPage + 1, sorted.length)}–{Math.min(page * perPage, sorted.length)} of {sorted.length} invoices
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`inv-page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 text-xs rounded border transition-colors ${
                page === i + 1 ? 'bg-primary text-white border-primary' : 'border-border hover:bg-muted'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-2 py-1 text-xs rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}